import { useEffect, useState } from "react";
import { Copy, Layers, X, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import { LoadingState } from "@/components/ui/StateBoundaries";
import CustomReportTable, { cellText } from "@/components/reports/CustomReportTable";
import CustomReportRange, { presetRange } from "@/components/reports/CustomReportRange";
import {
  downloadBlobFile,
  drilldownReport,
  duplicateCustomReport,
  exportCustomReportBlob,
  failureText,
  listCustomReports,
  runCustomReport,
} from "@/lib/customReportsApi";

/**
 * Reports → Custom reports: run a report the owner built in Settings → Reports
 * (owner, 2026-10-24, v2.1).
 * Features:
 * - Dynamic execution of saved report definitions and templates.
 * - Drill-down drawer showing transactional item lines for any classification bucket.
 * - Excel (.xlsx) and CSV exports.
 * - Report duplication / cloning.
 * - Optional Prior Period Comparison.
 */
export default function CustomReportsView() {
  const { token, settings, permissions } = usePOS();
  const canExport = !Array.isArray(permissions) || permissions.includes("reports.export");
  const canBuild = !Array.isArray(permissions) || permissions.includes("reports.custom");
  const dayStart = settings?.business_day_start || settings?.businessDayStart || "06:00";

  const [reports, setReports] = useState(null);
  const [listError, setListError] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [range, setRange] = useState(() => ({ preset: "today", ...presetRange("today", dayStart) }));
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Drilldown Drawer State
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownError, setDrilldownError] = useState(null);

  // Comparison State
  const [comparePrior, setComparePrior] = useState(false);
  const [priorResult, setPriorResult] = useState(null);

  useEffect(() => {
    let live = true;
    listCustomReports(token)
      .then(list => {
        if (!live) return;
        setReports(list);
        setChosen(current => current ?? list[0]?.id ?? null);
      })
      .catch(failure => live && setListError(failureText(failure, "Could not load the custom reports")));
    return () => { live = false; };
  }, [token]);

  useEffect(() => {
    if (!chosen || !range.fromDate || !range.toDate) return undefined;
    let live = true;
    setLoading(true);
    setError(null);
    runCustomReport(chosen, range, token)
      .then(body => live && setResult(body))
      .catch(failure => {
        if (!live) return;
        setResult(null);
        setError(failureText(failure, "Could not run this report"));
      })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [chosen, range, token]);

  const current = reports?.find(r => r.id === chosen);

  const handleExportXlsx = async () => {
    if (!chosen) return;
    try {
      const resp = await exportCustomReportBlob(chosen, "xlsx", range, token);
      const filename = `restoyaar-${current?.name || "custom"}-${range.fromDate}-to-${range.toDate}.xlsx`;
      downloadBlobFile(resp, filename);
      toast.success("Excel workbook downloaded");
    } catch (e) {
      toast.error(failureText(e, "Export to Excel failed"));
    }
  };

  const handleDuplicate = async () => {
    if (!chosen || !canBuild) return;
    try {
      const copyName = `${current?.name || "Report"} (Copy)`;
      const dup = await duplicateCustomReport(chosen, copyName, token);
      setReports(list => [...list, dup].sort((a, b) => a.name.localeCompare(b.name)));
      setChosen(dup.id);
      toast.success(`Duplicated as "${dup.name}"`);
    } catch (e) {
      toast.error(failureText(e, "Could not duplicate report"));
    }
  };

  const handleCellClick = async bucketValue => {
    if (!chosen || !bucketValue || bucketValue === "—") return;
    setDrilldownLoading(true);
    setDrilldownError(null);
    setDrilldownData(null);
    try {
      const res = await drilldownReport(chosen, String(bucketValue), range, token);
      setDrilldownData(res);
    } catch (e) {
      setDrilldownError(failureText(e, "Could not load transaction drilldown"));
    } finally {
      setDrilldownLoading(false);
    }
  };

  if (listError) return <p className="text-body text-destructive" role="alert" data-testid="custom-reports-list-error">{listError}</p>;
  if (!reports) return <LoadingState message="Reading custom reports…" testId="custom-reports-loading" />;
  if (!reports.length)
    return (
      <p className="p-base text-body text-muted-foreground" data-testid="custom-reports-none">
        No custom reports yet. The owner builds them in Settings → Reports.
      </p>
    );

  return (
    <section className="space-y-base relative" data-testid="custom-reports-view">
      <div className="flex flex-wrap items-center justify-between gap-snug">
        <div className="flex flex-wrap items-center gap-snug">
          <select
            value={chosen ?? ""}
            onChange={event => { setResult(null); setChosen(Number(event.target.value)); }}
            className="rounded border bg-background px-2 py-1 text-body font-medium"
            aria-label="Custom report"
            data-testid="custom-reports-select"
          >
            {reports.map(r => (
              <option key={r.id} value={r.id}>
                {r.name} {r.is_template ? "★ (Template)" : ""}
              </option>
            ))}
          </select>

          {current?.is_template && (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-micro font-medium bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-950 dark:text-amber-200">
              Template
            </span>
          )}

          {canBuild && chosen && (
            <Button variant="outline" size="sm" onClick={handleDuplicate} title="Duplicate this report as a copy">
              <Copy className="w-3.5 h-3.5 mr-tight" /> Duplicate
            </Button>
          )}

          {result?.date_range !== false && <CustomReportRange value={range} onChange={setRange} dayStart={dayStart} />}
        </div>
      </div>

      {error && <p className="text-body text-destructive" role="alert" data-testid="custom-reports-error">{error}</p>}
      {loading && !result && <LoadingState message="Running report…" testId="custom-reports-running" />}

      {result && !error && (
        <CustomReportTable
          result={result}
          canExport={canExport}
          filename={`restoyaar-${current?.name || "custom"}-${range.fromDate}-to-${range.toDate}`}
          testId="custom-reports-table"
          onCellClick={handleCellClick}
          onExportXlsx={handleExportXlsx}
        />
      )}

      {/* Transaction Drill-Down Drawer / Modal */}
      {(drilldownLoading || drilldownData || drilldownError) && (
        <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-card border-l shadow-2xl flex flex-col p-4 animate-in slide-in-from-right duration-200">
          <div className="flex items-center justify-between border-b pb-3 mb-3">
            <div>
              <h4 className="text-body font-bold flex items-center gap-2">
                <Layers className="w-4 h-4 text-primary" />
                Transaction Drilldown: {drilldownData?.bucket || "Loading..."}
              </h4>
              <p className="text-micro text-muted-foreground">
                Underlying transaction lines ({drilldownData?.count ?? 0} rows found)
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setDrilldownData(null); setDrilldownError(null); }}
              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {drilldownLoading && <LoadingState message="Fetching raw transaction items..." />}
          {drilldownError && <p className="text-body text-destructive p-3">{drilldownError}</p>}

          {drilldownData && (
            <div className="flex-1 overflow-auto text-meta">
              <table className="w-full border-collapse">
                <thead className="bg-muted sticky top-0 text-left text-micro font-semibold uppercase text-muted-foreground">
                  <tr>
                    <th className="p-2">Date / Bill</th>
                    <th className="p-2">Table</th>
                    <th className="p-2">Item</th>
                    <th className="p-2 text-right">Qty</th>
                    <th className="p-2 text-right">Rate</th>
                    <th className="p-2 text-right">Gross</th>
                    <th className="p-2 text-center">NC</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {drilldownData.rows.map((row, idx) => (
                    <tr key={idx} className="hover:bg-muted/40">
                      <td className="p-2 font-mono text-micro">
                        {row.order_date} #{row.bill_number || row.order_number}
                      </td>
                      <td className="p-2">{row.table_name || "—"}</td>
                      <td className="p-2 font-medium">{row.item_name}</td>
                      <td className="p-2 text-right tabular-nums">{row.quantity}</td>
                      <td className="p-2 text-right tabular-nums">₹{cellText("money", row.unit_price)}</td>
                      <td className="p-2 text-right tabular-nums font-semibold">₹{cellText("money", row.line_gross)}</td>
                      <td className="p-2 text-center">
                        {row.is_nc ? (
                          <span className="px-1.5 py-0.5 rounded text-micro bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                            NC
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
